/**
 * RightSidebar — the unified Phase 10 sidebar.
 *
 * Mounts inside the InspectionDashboard outer shell and persists across every
 * filter change (the dashboard's outer shell rule). Three tabs:
 *
 *   Calendar  — merged timeline (Google + Office365 + CRM meetings)
 *   Email     — per-binding filtered inbox(es)
 *   Tasks     — future home for CRM tasks; placeholder for now
 *
 * Layout:
 *   Desktop ≥lg → 380px right-docked column, collapsible into a 56px icon dock.
 *   Mobile  <lg → Radix Dialog as a bottom sheet with 3 snap points.
 *
 * NEVER a modal with backdrop-blur. NEVER full-screen on desktop. The whole
 * point is to live alongside the map.
 */
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Calendar as CalendarIcon, Mail, CheckSquare, ChevronRight, ChevronLeft, X } from 'lucide-react';
import { clsx } from 'clsx';
import { SidebarTabs } from './SidebarTabs';

type SidebarTab = 'calendar' | 'email' | 'tasks';

interface Props {
  /** Optional: force-open on mobile from a parent button (e.g. dashboard header). */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

export function RightSidebar({ mobileOpen, onMobileOpenChange }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [active, setActive] = useState<SidebarTab>('calendar');

  return (
    <>
      {/* DESKTOP: docked right column, flex sibling — never overlays. */}
      <aside
        className={clsx(
          'relative hidden h-full shrink-0 flex-col overflow-hidden border-l border-white/10 bg-neutral-950/90 transition-[width] duration-300 lg:flex',
          collapsed ? 'w-[56px]' : 'w-[380px]',
        )}
        aria-label="Calendar and email sidebar"
      >
        {/* Collapse handle */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="absolute -left-3 top-4 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-neutral-900 text-neutral-400 shadow-lg transition hover:text-neutral-100"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronLeft className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>

        {collapsed ? (
          <DockedRail active={active} onSelect={(t) => { setActive(t); setCollapsed(false); }} />
        ) : (
          <SidebarTabs active={active} onChange={setActive} />
        )}
      </aside>

      {/* MOBILE: bottom-sheet dialog. Soft backdrop, not a blur wall. */}
      <Dialog.Root open={mobileOpen ?? false} onOpenChange={(o) => onMobileOpenChange?.(o)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-neutral-950/40 lg:hidden" />
          <Dialog.Content
            className="fixed inset-x-0 bottom-0 z-50 flex h-[80vh] flex-col rounded-t-2xl border-t border-white/10 bg-neutral-950 shadow-2xl lg:hidden"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="text-sm font-semibold">Workspace</div>
              <Dialog.Close className="rounded-full p-1 text-neutral-400 hover:bg-white/5 hover:text-neutral-100" aria-label="Close">
                <X className="h-4 w-4" />
              </Dialog.Close>
            </div>
            <SidebarTabs active={active} onChange={setActive} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function DockedRail({
  active,
  onSelect,
}: {
  active: SidebarTab;
  onSelect: (t: SidebarTab) => void;
}) {
  const items: { id: SidebarTab; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
    { id: 'calendar', icon: CalendarIcon, label: 'Calendar' },
    { id: 'email', icon: Mail, label: 'Email' },
    { id: 'tasks', icon: CheckSquare, label: 'Tasks' },
  ];
  return (
    <nav className="flex flex-col items-center gap-1 py-4">
      {items.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          aria-label={label}
          title={label}
          className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-xl transition',
            id === active
              ? 'bg-brand-600/15 text-brand-200'
              : 'text-neutral-500 hover:bg-white/5 hover:text-neutral-200',
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </nav>
  );
}
